/**
 * GenViz / SmartFence — Catalogue des codes d'erreur.
 *
 * C'est LE catalogue que l'application traduit : la réponse d'API ne contient jamais de message
 * lisible, seulement un `code` de cette liste. La clé i18n associée est `erreurs.<CODE>` ;
 * les trois applications (mobile, back-office, e-mails backend) partagent ce même espace de clés.
 *
 * Chaque entrée porte :
 *  - le statut HTTP retenu par le contrat d'API,
 *  - le domaine fonctionnel,
 *  - le comportement de champ attendu par la spec écran par écran (effacer / conserver la saisie),
 *  - le code équivalent du document `docs/02-contrat-api.md` (nomenclature anglaise), pour la
 *    traçabilité endpoint par endpoint.
 *
 * La valeur transmise sur le fil est le code FRANÇAIS ci-dessous.
 */

/* -------------------------------------------------------------------------- */
/* Codes                                                                       */
/* -------------------------------------------------------------------------- */

export const CodeErreur = {
  /* --- Authentification, session, compte ---------------------------------- */
  NON_AUTHENTIFIE: 'NON_AUTHENTIFIE',
  JETON_ACCES_INVALIDE: 'JETON_ACCES_INVALIDE',
  JETON_ACCES_EXPIRE: 'JETON_ACCES_EXPIRE',
  JETON_RAFRAICHISSEMENT_INVALIDE: 'JETON_RAFRAICHISSEMENT_INVALIDE',
  JETON_RAFRAICHISSEMENT_REJOUE: 'JETON_RAFRAICHISSEMENT_REJOUE',
  ADRESSE_EMAIL_INCONNUE: 'ADRESSE_EMAIL_INCONNUE',
  MOT_DE_PASSE_INCORRECT: 'MOT_DE_PASSE_INCORRECT',
  IDENTIFIANTS_INVALIDES: 'IDENTIFIANTS_INVALIDES',
  COMPTE_DESACTIVE: 'COMPTE_DESACTIVE',
  COMPTE_VERROUILLE: 'COMPTE_VERROUILLE',
  MOT_DE_PASSE_TROP_FAIBLE: 'MOT_DE_PASSE_TROP_FAIBLE',
  CONFIRMATION_MOT_DE_PASSE_INCORRECTE: 'CONFIRMATION_MOT_DE_PASSE_INCORRECTE',
  MOT_DE_PASSE_ACTUEL_INVALIDE: 'MOT_DE_PASSE_ACTUEL_INVALIDE',
  MOT_DE_PASSE_IDENTIQUE_A_ANCIEN: 'MOT_DE_PASSE_IDENTIQUE_A_ANCIEN',
  JETON_REINITIALISATION_EXPIRE: 'JETON_REINITIALISATION_EXPIRE',
  JETON_REINITIALISATION_UTILISE: 'JETON_REINITIALISATION_UTILISE',
  ROLE_NON_AUTORISE: 'ROLE_NON_AUTORISE',
  AUCUNE_EXPLOITATION: 'AUCUNE_EXPLOITATION',

  /* --- Exploitation et comptes -------------------------------------------- */
  ADRESSE_EMAIL_DEJA_UTILISEE: 'ADRESSE_EMAIL_DEJA_UTILISEE',
  ADRESSE_EMAIL_INCONNUE_OU_DEJA_UTILISEE: 'ADRESSE_EMAIL_INCONNUE_OU_DEJA_UTILISEE',
  ADRESSE_EMAIL_INVALIDE: 'ADRESSE_EMAIL_INVALIDE',
  ADRESSE_EMAIL_DEJA_MEMBRE: 'ADRESSE_EMAIL_DEJA_MEMBRE',
  ADRESSE_EMAIL_AUTRE_EXPLOITATION: 'ADRESSE_EMAIL_AUTRE_EXPLOITATION',
  CONFLIT_CREATION_EXPLOITATION: 'CONFLIT_CREATION_EXPLOITATION',
  UTILISATEUR_INTROUVABLE: 'UTILISATEUR_INTROUVABLE',
  UTILISATEUR_HORS_EXPLOITATION: 'UTILISATEUR_HORS_EXPLOITATION',
  UTILISATEUR_DEJA_DANS_EXPLOITATION: 'UTILISATEUR_DEJA_DANS_EXPLOITATION',
  RETRAIT_DE_SOI_INTERDIT: 'RETRAIT_DE_SOI_INTERDIT',
  DERNIER_ADMINISTRATEUR: 'DERNIER_ADMINISTRATEUR',

  /* --- Invitations --------------------------------------------------------- */
  CODE_INVITATION_INVALIDE: 'CODE_INVITATION_INVALIDE',
  INVITATION_EXPIREE: 'INVITATION_EXPIREE',
  INVITATION_DEJA_UTILISEE: 'INVITATION_DEJA_UTILISEE',
  INVITATION_INTROUVABLE: 'INVITATION_INTROUVABLE',
  INVITATION_DEJA_EN_ATTENTE: 'INVITATION_DEJA_EN_ATTENTE',

  /* --- Transfert d'administrateur ------------------------------------------ */
  TRANSFERT_DEJA_EN_ATTENTE: 'TRANSFERT_DEJA_EN_ATTENTE',
  TRANSFERT_NON_EN_ATTENTE: 'TRANSFERT_NON_EN_ATTENTE',
  TRANSFERT_EXPIRE: 'TRANSFERT_EXPIRE',
  NON_DESTINATAIRE_TRANSFERT: 'NON_DESTINATAIRE_TRANSFERT',
  DESTINATAIRE_DEJA_ADMINISTRATEUR: 'DESTINATAIRE_DEJA_ADMINISTRATEUR',

  /* --- Appareils ----------------------------------------------------------- */
  APPAREIL_INTROUVABLE: 'APPAREIL_INTROUVABLE',
  APPAREIL_INEXISTANT: 'APPAREIL_INEXISTANT',
  APPAREIL_DEJA_ENREGISTRE: 'APPAREIL_DEJA_ENREGISTRE',
  NUMERO_SERIE_INCORRECT: 'NUMERO_SERIE_INCORRECT',
  NUMERO_SERIE_DUPLIQUE: 'NUMERO_SERIE_DUPLIQUE',
  NOM_APPAREIL_DEJA_UTILISE: 'NOM_APPAREIL_DEJA_UTILISE',
  APPAREIL_HORS_EXPLOITATION: 'APPAREIL_HORS_EXPLOITATION',
  APPAREIL_HORS_LIGNE: 'APPAREIL_HORS_LIGNE',
  /** Session de mesure demandée sur un électrificateur éteint : il n'y a rien à mesurer. */
  APPAREIL_ETEINT: 'APPAREIL_ETEINT',
  APPAREIL_COMMANDES_EN_ATTENTE: 'APPAREIL_COMMANDES_EN_ATTENTE',
  APPAREIL_NON_ASSOCIE: 'APPAREIL_NON_ASSOCIE',
  CLE_APPAIRAGE_INVALIDE: 'CLE_APPAIRAGE_INVALIDE',
  JETON_APPAIRAGE_INVALIDE: 'JETON_APPAIRAGE_INVALIDE',
  JETON_APPAIRAGE_EXPIRE: 'JETON_APPAIRAGE_EXPIRE',
  ENREGISTREMENT_CONCURRENT: 'ENREGISTREMENT_CONCURRENT',

  /* --- Commandes et session de mesure -------------------------------------- */
  COMMANDE_DEJA_EN_COURS: 'COMMANDE_DEJA_EN_COURS',
  COMMANDE_INTROUVABLE: 'COMMANDE_INTROUVABLE',
  COMMANDE_NON_SUPPORTEE: 'COMMANDE_NON_SUPPORTEE',
  COMMANDE_EXPIREE: 'COMMANDE_EXPIREE',
  COMMANDE_DEJA_TERMINEE: 'COMMANDE_DEJA_TERMINEE',
  AUCUNE_SESSION_ACTIVE: 'AUCUNE_SESSION_ACTIVE',
  SESSION_EXPIREE: 'SESSION_EXPIREE',

  /* --- Alertes -------------------------------------------------------------- */
  ALERTE_INTROUVABLE: 'ALERTE_INTROUVABLE',
  ALERTE_HORS_EXPLOITATION: 'ALERTE_HORS_EXPLOITATION',
  ALERTE_NON_MOUVEMENT: 'ALERTE_NON_MOUVEMENT',
  AUCUNE_POSITION_COURANTE: 'AUCUNE_POSITION_COURANTE',

  /* --- Paramètres et préférences -------------------------------------------- */
  VALEUR_HORS_BORNES: 'VALEUR_HORS_BORNES',
  VALEUR_NON_AUTORISEE: 'VALEUR_NON_AUTORISEE',
  FENETRE_NON_SUPPORTEE: 'FENETRE_NON_SUPPORTEE',
  FUSEAU_HORAIRE_INVALIDE: 'FUSEAU_HORAIRE_INVALIDE',
  CANAL_INDISPONIBLE: 'CANAL_INDISPONIBLE',

  /* --- Ingestion (objet) ---------------------------------------------------- */
  OBJET_NON_AUTHENTIFIE: 'OBJET_NON_AUTHENTIFIE',
  OBJET_REVOQUE: 'OBJET_REVOQUE',
  IDENTITE_OBJET_DIVERGENTE: 'IDENTITE_OBJET_DIVERGENTE',
  REJEU_DETECTE: 'REJEU_DETECTE',
  CHARGE_UTILE_TROP_VOLUMINEUSE: 'CHARGE_UTILE_TROP_VOLUMINEUSE',
  SCHEMA_INVALIDE: 'SCHEMA_INVALIDE',

  /* --- Génériques ----------------------------------------------------------- */
  CHAMP_OBLIGATOIRE: 'CHAMP_OBLIGATOIRE',
  ERREUR_VALIDATION: 'ERREUR_VALIDATION',
  CONFIRMATION_REQUISE: 'CONFIRMATION_REQUISE',
  PRECONDITION_ECHOUEE: 'PRECONDITION_ECHOUEE',
  RESSOURCE_INTROUVABLE: 'RESSOURCE_INTROUVABLE',
  DOCUMENTATION_INDISPONIBLE: 'DOCUMENTATION_INDISPONIBLE',
  TROP_DE_REQUETES: 'TROP_DE_REQUETES',
  SERVICE_INDISPONIBLE: 'SERVICE_INDISPONIBLE',
  ERREUR_INTERNE: 'ERREUR_INTERNE',
  VERSION_APP_OBSOLETE: 'VERSION_APP_OBSOLETE',
} as const;
export type CodeErreur = (typeof CodeErreur)[keyof typeof CodeErreur];

/**
 * Codes produits par l'APPLICATION seule (jamais renvoyés par le serveur), présents dans la spec
 * écran par écran. Ils partagent l'espace de clés i18n `erreurs.<CODE>`.
 */
export const CodeErreurClient = {
  /** « QR Code illisible » (l.503) — active la saisie manuelle du numéro de série. */
  QR_ILLISIBLE: 'QR_ILLISIBLE',
  /** « Erreur de connexion réseau » (l.512) — on reste sur l'écran SANS effacer le champ. */
  ERREUR_RESEAU: 'ERREUR_RESEAU',
  /** Requête interrompue par un délai d'attente côté application. */
  DELAI_DEPASSE: 'DELAI_DEPASSE',
  /** Aucune donnée en cache et pas de réseau (mode dégradé). */
  HORS_LIGNE: 'HORS_LIGNE',
  /** Permission Android refusée (caméra, notifications, localisation). */
  PERMISSION_REFUSEE: 'PERMISSION_REFUSEE',
} as const;
export type CodeErreurClient = (typeof CodeErreurClient)[keyof typeof CodeErreurClient];

/** Union des codes traduisibles côté application. */
export type CodeErreurTraduisible = CodeErreur | CodeErreurClient;

/* -------------------------------------------------------------------------- */
/* Métadonnées                                                                 */
/* -------------------------------------------------------------------------- */

/** Domaine fonctionnel d'une erreur — sert au routage et au regroupement des catalogues i18n. */
export const DomaineErreur = {
  AUTH: 'AUTH',
  EXPLOITATION: 'EXPLOITATION',
  UTILISATEUR: 'UTILISATEUR',
  INVITATION: 'INVITATION',
  TRANSFERT_ADMIN: 'TRANSFERT_ADMIN',
  APPAREIL: 'APPAREIL',
  COMMANDE: 'COMMANDE',
  ALERTE: 'ALERTE',
  PARAMETRE: 'PARAMETRE',
  INGESTION: 'INGESTION',
  GENERIQUE: 'GENERIQUE',
} as const;
export type DomaineErreur = (typeof DomaineErreur)[keyof typeof DomaineErreur];

/**
 * Traitement du champ de saisie après l'erreur, tel que décrit écran par écran par la spec.
 * C'est une consigne d'ergonomie, pas une chaîne d'affichage.
 */
export const ComportementChamp = {
  /** Le champ est vidé dès l'affichage du message (ex. 01A « Appareil introuvable »). */
  EFFACER_IMMEDIATEMENT: 'EFFACER_IMMEDIATEMENT',
  /** Le champ et le message sont effacés à la nouvelle saisie utilisateur (ex. 04A.4). */
  EFFACER_A_LA_SAISIE: 'EFFACER_A_LA_SAISIE',
  /** La saisie est conservée (ex. changement de mot de passe en 05A). */
  CONSERVER: 'CONSERVER',
  /** Sans objet : l'erreur n'est pas liée à un champ de formulaire. */
  SANS_OBJET: 'SANS_OBJET',
} as const;
export type ComportementChamp =
  (typeof ComportementChamp)[keyof typeof ComportementChamp];

/** Définition complète d'un code d'erreur. */
export interface DefinitionErreur {
  code: CodeErreur;
  /** Statut HTTP renvoyé par l'API. */
  http: number;
  domaine: DomaineErreur;
  /** Clé de traduction, identique dans les trois applications. */
  cle_i18n: string;
  /** Comportement attendu du champ de saisie concerné. */
  comportement_champ: ComportementChamp;
  /** Code équivalent dans docs/02-contrat-api.md (nomenclature anglaise). */
  code_contrat_api: string;
  /** true si l'utilisateur peut réessayer tel quel (réseau, verrou temporaire, service occupé). */
  reessayable: boolean;
}

type MetaErreur = Omit<DefinitionErreur, 'code' | 'cle_i18n'>;

const META: Readonly<Record<CodeErreur, MetaErreur>> = {
  /* --- Authentification, session, compte ---------------------------------- */
  NON_AUTHENTIFIE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'UNAUTHENTICATED',
    reessayable: false,
  },
  JETON_ACCES_INVALIDE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ACCESS_TOKEN_INVALID',
    reessayable: false,
  },
  JETON_ACCES_EXPIRE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ACCESS_TOKEN_EXPIRED',
    reessayable: true,
  },
  JETON_RAFRAICHISSEMENT_INVALIDE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'REFRESH_TOKEN_INVALID',
    reessayable: false,
  },
  JETON_RAFRAICHISSEMENT_REJOUE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'REFRESH_TOKEN_REUSED',
    reessayable: false,
  },
  ADRESSE_EMAIL_INCONNUE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'AUTH_EMAIL_UNKNOWN',
    reessayable: false,
  },
  MOT_DE_PASSE_INCORRECT: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'AUTH_BAD_PASSWORD',
    reessayable: false,
  },
  IDENTIFIANTS_INVALIDES: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'AUTH_INVALID_CREDENTIALS',
    reessayable: false,
  },
  COMPTE_DESACTIVE: {
    http: 403,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ACCOUNT_DISABLED',
    reessayable: false,
  },
  COMPTE_VERROUILLE: {
    http: 429,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ACCOUNT_LOCKED',
    reessayable: true,
  },
  MOT_DE_PASSE_TROP_FAIBLE: {
    http: 422,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'PASSWORD_TOO_WEAK',
    reessayable: false,
  },
  CONFIRMATION_MOT_DE_PASSE_INCORRECTE: {
    http: 422,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'PASSWORD_CONFIRMATION_MISMATCH',
    reessayable: false,
  },
  MOT_DE_PASSE_ACTUEL_INVALIDE: {
    http: 401,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'CURRENT_PASSWORD_INVALID',
    reessayable: false,
  },
  MOT_DE_PASSE_IDENTIQUE_A_ANCIEN: {
    http: 422,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'PASSWORD_SAME_AS_OLD',
    reessayable: false,
  },
  JETON_REINITIALISATION_EXPIRE: {
    http: 410,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'RESET_TOKEN_EXPIRED',
    reessayable: false,
  },
  JETON_REINITIALISATION_UTILISE: {
    http: 409,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'RESET_TOKEN_USED',
    reessayable: false,
  },
  ROLE_NON_AUTORISE: {
    http: 403,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'FORBIDDEN_ROLE',
    reessayable: false,
  },
  AUCUNE_EXPLOITATION: {
    http: 403,
    domaine: DomaineErreur.AUTH,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NO_EXPLOITATION',
    reessayable: false,
  },

  /* --- Exploitation et comptes -------------------------------------------- */
  ADRESSE_EMAIL_DEJA_UTILISEE: {
    http: 409,
    domaine: DomaineErreur.EXPLOITATION,
    comportement_champ: ComportementChamp.EFFACER_IMMEDIATEMENT,
    code_contrat_api: 'EMAIL_ALREADY_USED',
    reessayable: false,
  },
  ADRESSE_EMAIL_INCONNUE_OU_DEJA_UTILISEE: {
    http: 409,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'EMAIL_UNKNOWN_OR_USED',
    reessayable: false,
  },
  ADRESSE_EMAIL_INVALIDE: {
    http: 422,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'EMAIL_INVALID',
    reessayable: false,
  },
  ADRESSE_EMAIL_DEJA_MEMBRE: {
    http: 409,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'EMAIL_ALREADY_MEMBER',
    reessayable: false,
  },
  ADRESSE_EMAIL_AUTRE_EXPLOITATION: {
    http: 409,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'EMAIL_IN_OTHER_EXPLOITATION',
    reessayable: false,
  },
  CONFLIT_CREATION_EXPLOITATION: {
    http: 409,
    domaine: DomaineErreur.EXPLOITATION,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'EXPLOITATION_CREATION_CONFLICT',
    reessayable: false,
  },
  UTILISATEUR_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'USER_NOT_FOUND',
    reessayable: false,
  },
  UTILISATEUR_HORS_EXPLOITATION: {
    http: 403,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'USER_NOT_IN_EXPLOITATION',
    reessayable: false,
  },
  UTILISATEUR_DEJA_DANS_EXPLOITATION: {
    http: 409,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'USER_ALREADY_IN_EXPLOITATION',
    reessayable: false,
  },
  RETRAIT_DE_SOI_INTERDIT: {
    http: 409,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'CANNOT_REMOVE_SELF',
    reessayable: false,
  },
  DERNIER_ADMINISTRATEUR: {
    http: 409,
    domaine: DomaineErreur.UTILISATEUR,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'LAST_ADMIN',
    reessayable: false,
  },

  /* --- Invitations --------------------------------------------------------- */
  CODE_INVITATION_INVALIDE: {
    http: 404,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'INVITATION_INVALID',
    reessayable: false,
  },
  INVITATION_EXPIREE: {
    http: 410,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'INVITATION_EXPIRED',
    reessayable: false,
  },
  INVITATION_DEJA_UTILISEE: {
    http: 409,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'INVITATION_ALREADY_USED',
    reessayable: false,
  },
  INVITATION_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'INVITATION_NOT_FOUND',
    reessayable: false,
  },
  INVITATION_DEJA_EN_ATTENTE: {
    http: 409,
    domaine: DomaineErreur.INVITATION,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'INVITATION_ALREADY_PENDING',
    reessayable: false,
  },

  /* --- Transfert d'administrateur ------------------------------------------ */
  TRANSFERT_DEJA_EN_ATTENTE: {
    http: 409,
    domaine: DomaineErreur.TRANSFERT_ADMIN,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'TRANSFER_ALREADY_PENDING',
    reessayable: false,
  },
  TRANSFERT_NON_EN_ATTENTE: {
    http: 409,
    domaine: DomaineErreur.TRANSFERT_ADMIN,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'TRANSFER_NOT_PENDING',
    reessayable: false,
  },
  TRANSFERT_EXPIRE: {
    http: 410,
    domaine: DomaineErreur.TRANSFERT_ADMIN,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'TRANSFER_EXPIRED',
    reessayable: false,
  },
  NON_DESTINATAIRE_TRANSFERT: {
    http: 403,
    domaine: DomaineErreur.TRANSFERT_ADMIN,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NOT_TRANSFER_RECIPIENT',
    reessayable: false,
  },
  DESTINATAIRE_DEJA_ADMINISTRATEUR: {
    http: 422,
    domaine: DomaineErreur.TRANSFERT_ADMIN,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'TARGET_IS_ADMIN',
    reessayable: false,
  },

  /* --- Appareils ----------------------------------------------------------- */
  APPAREIL_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.EFFACER_IMMEDIATEMENT,
    code_contrat_api: 'DEVICE_NOT_IN_STOCK',
    reessayable: false,
  },
  APPAREIL_INEXISTANT: {
    http: 404,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_NOT_FOUND',
    reessayable: false,
  },
  APPAREIL_DEJA_ENREGISTRE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.EFFACER_IMMEDIATEMENT,
    code_contrat_api: 'DEVICE_ALREADY_REGISTERED',
    reessayable: false,
  },
  NUMERO_SERIE_INCORRECT: {
    http: 422,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'DEVICE_SERIAL_INVALID',
    reessayable: false,
  },
  NUMERO_SERIE_DUPLIQUE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'SERIAL_DUPLICATE',
    reessayable: false,
  },
  NOM_APPAREIL_DEJA_UTILISE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.EFFACER_A_LA_SAISIE,
    code_contrat_api: 'DEVICE_NAME_ALREADY_USED',
    reessayable: false,
  },
  APPAREIL_HORS_EXPLOITATION: {
    http: 403,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_NOT_IN_EXPLOITATION',
    reessayable: false,
  },
  APPAREIL_HORS_LIGNE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_OFFLINE',
    reessayable: true,
  },
  APPAREIL_ETEINT: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_POWERED_OFF',
    reessayable: false,
  },
  APPAREIL_COMMANDES_EN_ATTENTE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_HAS_PENDING_COMMANDS',
    reessayable: true,
  },
  APPAREIL_NON_ASSOCIE: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_NOT_BOUND',
    reessayable: false,
  },
  CLE_APPAIRAGE_INVALIDE: {
    http: 403,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.EFFACER_IMMEDIATEMENT,
    code_contrat_api: 'PAIRING_KEY_INVALID',
    reessayable: false,
  },
  JETON_APPAIRAGE_INVALIDE: {
    http: 401,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'PAIRING_TOKEN_INVALID',
    reessayable: false,
  },
  JETON_APPAIRAGE_EXPIRE: {
    http: 410,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'PAIRING_TOKEN_EXPIRED',
    reessayable: false,
  },
  ENREGISTREMENT_CONCURRENT: {
    http: 409,
    domaine: DomaineErreur.APPAREIL,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'CONCURRENT_REGISTRATION',
    reessayable: true,
  },

  /* --- Commandes et session de mesure -------------------------------------- */
  COMMANDE_DEJA_EN_COURS: {
    http: 409,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'COMMAND_ALREADY_PENDING',
    reessayable: true,
  },
  COMMANDE_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'COMMAND_NOT_FOUND',
    reessayable: false,
  },
  COMMANDE_NON_SUPPORTEE: {
    http: 422,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'COMMAND_NOT_SUPPORTED',
    reessayable: false,
  },
  COMMANDE_EXPIREE: {
    http: 410,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'COMMAND_EXPIRED',
    reessayable: true,
  },
  COMMANDE_DEJA_TERMINEE: {
    http: 409,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'COMMAND_ALREADY_TERMINAL',
    reessayable: false,
  },
  AUCUNE_SESSION_ACTIVE: {
    http: 409,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NO_ACTIVE_SESSION',
    reessayable: false,
  },
  SESSION_EXPIREE: {
    http: 410,
    domaine: DomaineErreur.COMMANDE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'SESSION_EXPIRED',
    reessayable: true,
  },

  /* --- Alertes -------------------------------------------------------------- */
  ALERTE_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.ALERTE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ALERT_NOT_FOUND',
    reessayable: false,
  },
  ALERTE_HORS_EXPLOITATION: {
    http: 403,
    domaine: DomaineErreur.ALERTE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'ALERT_NOT_IN_EXPLOITATION',
    reessayable: false,
  },
  ALERTE_NON_MOUVEMENT: {
    http: 409,
    domaine: DomaineErreur.ALERTE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NOT_A_MOVEMENT_ALERT',
    reessayable: false,
  },
  AUCUNE_POSITION_COURANTE: {
    http: 409,
    domaine: DomaineErreur.ALERTE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NO_CURRENT_POSITION',
    reessayable: true,
  },

  /* --- Paramètres et préférences -------------------------------------------- */
  VALEUR_HORS_BORNES: {
    http: 422,
    domaine: DomaineErreur.PARAMETRE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'VALUE_OUT_OF_RANGE',
    reessayable: false,
  },
  VALEUR_NON_AUTORISEE: {
    http: 422,
    domaine: DomaineErreur.PARAMETRE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'VALUE_NOT_ALLOWED',
    reessayable: false,
  },
  FENETRE_NON_SUPPORTEE: {
    http: 422,
    domaine: DomaineErreur.PARAMETRE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'WINDOW_NOT_SUPPORTED',
    reessayable: false,
  },
  FUSEAU_HORAIRE_INVALIDE: {
    http: 422,
    domaine: DomaineErreur.PARAMETRE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'TIMEZONE_INVALID',
    reessayable: false,
  },
  CANAL_INDISPONIBLE: {
    http: 422,
    domaine: DomaineErreur.PARAMETRE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'CHANNEL_UNAVAILABLE',
    reessayable: false,
  },

  /* --- Ingestion (objet) ---------------------------------------------------- */
  OBJET_NON_AUTHENTIFIE: {
    http: 401,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_UNAUTHENTICATED',
    reessayable: false,
  },
  OBJET_REVOQUE: {
    http: 403,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_REVOKED',
    reessayable: false,
  },
  IDENTITE_OBJET_DIVERGENTE: {
    http: 403,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DEVICE_IDENTITY_MISMATCH',
    reessayable: false,
  },
  REJEU_DETECTE: {
    http: 409,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'REPLAY_DETECTED',
    reessayable: false,
  },
  CHARGE_UTILE_TROP_VOLUMINEUSE: {
    http: 413,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'PAYLOAD_TOO_LARGE',
    reessayable: false,
  },
  SCHEMA_INVALIDE: {
    http: 422,
    domaine: DomaineErreur.INGESTION,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'SCHEMA_INVALID',
    reessayable: false,
  },

  /* --- Génériques ----------------------------------------------------------- */
  CHAMP_OBLIGATOIRE: {
    http: 422,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'FIELD_REQUIRED',
    reessayable: false,
  },
  ERREUR_VALIDATION: {
    http: 422,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'VALIDATION_ERROR',
    reessayable: false,
  },
  CONFIRMATION_REQUISE: {
    http: 409,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'CONFIRMATION_REQUIRED',
    reessayable: true,
  },
  PRECONDITION_ECHOUEE: {
    http: 412,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'PRECONDITION_FAILED',
    reessayable: true,
  },
  RESSOURCE_INTROUVABLE: {
    http: 404,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'NOT_FOUND',
    reessayable: false,
  },
  DOCUMENTATION_INDISPONIBLE: {
    http: 404,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'DOCUMENTATION_NOT_AVAILABLE',
    reessayable: false,
  },
  TROP_DE_REQUETES: {
    http: 429,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'RATE_LIMITED',
    reessayable: true,
  },
  SERVICE_INDISPONIBLE: {
    http: 503,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'SERVICE_UNAVAILABLE',
    reessayable: true,
  },
  ERREUR_INTERNE: {
    http: 500,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.CONSERVER,
    code_contrat_api: 'INTERNAL_ERROR',
    reessayable: true,
  },
  VERSION_APP_OBSOLETE: {
    http: 426,
    domaine: DomaineErreur.GENERIQUE,
    comportement_champ: ComportementChamp.SANS_OBJET,
    code_contrat_api: 'APP_VERSION_UNSUPPORTED',
    reessayable: false,
  },
};

/** Catalogue complet, indexé par code. */
export const CATALOGUE_ERREURS: Readonly<Record<CodeErreur, DefinitionErreur>> =
  Object.freeze(
    Object.fromEntries(
      (Object.entries(META) as Array<[CodeErreur, MetaErreur]>).map(([code, meta]) => [
        code,
        { code, cle_i18n: cleI18nErreur(code), ...meta },
      ]),
    ) as Record<CodeErreur, DefinitionErreur>,
  );

/** Index inverse : code de `docs/02-contrat-api.md` (anglais) → code canonique français. */
export const CODE_ERREUR_PAR_CODE_CONTRAT: Readonly<Record<string, CodeErreur>> =
  Object.freeze(
    Object.fromEntries(
      (Object.entries(META) as Array<[CodeErreur, MetaErreur]>).map(([code, meta]) => [
        meta.code_contrat_api,
        code,
      ]),
    ) as Record<string, CodeErreur>,
  );

/* -------------------------------------------------------------------------- */
/* Charge utile d'erreur                                                       */
/* -------------------------------------------------------------------------- */

/** Erreur portant sur un champ précis d'une requête. */
export interface ErreurChamp {
  /** Chemin du champ fautif dans le corps de la requête, ex. `nom_exploitation`. */
  champ: string;
  code: CodeErreur;
  /** Contraintes utiles au message localisé (longueur max, bornes) — jamais du texte. */
  contexte?: Record<string, string | number | boolean | null>;
}

/**
 * Corps d'erreur unique de toute l'API (REST publique, ingestion, back-office).
 * Aucun champ ne contient de texte destiné à l'utilisateur : l'app traduit `code` via `cle_i18n`.
 */
export interface ReponseErreurApi {
  code: CodeErreur;
  /** Statut HTTP, dupliqué dans le corps pour les clients qui ne le propagent pas. */
  http: number;
  /** Identifiant de corrélation à donner au support. */
  trace_id?: string;
  /** Erreurs de validation détaillées, champ par champ. */
  champs?: ErreurChamp[];
  /** Données structurées propres à certains codes (bornes dépassées, délai d'attente…). */
  contexte?: Record<string, unknown>;
  /** Délai conseillé avant nouvelle tentative, en secondes (429, 503). */
  reessayer_dans_s?: number;
}

/* -------------------------------------------------------------------------- */
/* Utilitaires                                                                 */
/* -------------------------------------------------------------------------- */

/** Clé de traduction associée à un code (`erreurs.APPAREIL_INTROUVABLE`). */
export function cleI18nErreur(code: CodeErreurTraduisible): string {
  return `erreurs.${code}`;
}

/** Statut HTTP associé à un code d'erreur. */
export function httpDeCodeErreur(code: CodeErreur): number {
  return CATALOGUE_ERREURS[code].http;
}

/** Vrai si la valeur est un code d'erreur serveur connu. */
export function estCodeErreur(valeur: unknown): valeur is CodeErreur {
  return typeof valeur === 'string' && Object.prototype.hasOwnProperty.call(META, valeur);
}

/** Vrai si l'objet est une réponse d'erreur d'API bien formée. */
export function estReponseErreurApi(valeur: unknown): valeur is ReponseErreurApi {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const candidat = valeur as { code?: unknown; http?: unknown };
  return estCodeErreur(candidat.code) && typeof candidat.http === 'number';
}

/** Convertit un code de la nomenclature `docs/02-contrat-api.md` vers le code canonique. */
export function codeErreurDepuisContratApi(codeAnglais: string): CodeErreur | undefined {
  return CODE_ERREUR_PAR_CODE_CONTRAT[codeAnglais];
}

/** Construit une réponse d'erreur cohérente avec le catalogue. */
export function construireErreur(
  code: CodeErreur,
  options: Omit<ReponseErreurApi, 'code' | 'http'> = {},
): ReponseErreurApi {
  return { code, http: httpDeCodeErreur(code), ...options };
}
